"use client"

import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  Row,
  Col,
  Input,
  FormGroup,
  Label,
  Card,
  CardBody,
} from "reactstrap";
import { UploadCloud } from "react-feather";
import { createProduct, updateProduct } from "../../../../../api-handle/productApi";
import { removeFile, uploadMultipleFiles } from "../../../../../api-handle/fileApi";
import { sanitizeArabicFieldEvent } from "../../../../../Services/arabicInput";
import "./product.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ProductFormModal = ({ isOpen, toggle, productData, merchantId, onRefresh }) => {
  const [formData, setFormData] = useState(
    productData || {
      name: "",
      nameAr: "",
      description: "",
      descriptionAr: "",
      price: "",
      stock: "",
      images: [],
      tempImages: [],
      discount: 0,
      discountType: "fixed",
      colors: [],
      sizes: [],
    }
  );
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Character and number limits
  const limits = {
    name: 100,
    nameAr: 100,
    description: 500,
    descriptionAr: 500,
    price: 9999,
    stock: 9999,
    discount: 9999,
    colorStock: 9999,
    sizeStock: 9999,
    sizePrice: 9999,
  };

  // File size limit in bytes (3MB = 3 * 1024 * 1024 bytes)
  const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB

  const FILE_BASE_URL = process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL || "http://localhost:4001";

  useEffect(() => {
    if (isOpen) {
      if (productData) {
        setFormData({
          ...productData,
          price: productData.basePrice || "",
          colors: productData.colors || [],
          sizes: productData.sizes || [],
          tempImages: [],
        });
      } else {
        setFormData({
          name: "",
          nameAr: "",
          description: "",
          descriptionAr: "",
          price: "",
          stock: "",
          images: [],
          tempImages: [],
          discount: "",
          discountType: "fixed",
          colors: [],
          sizes: [],
        });
      }
      setErrors({});
    }
  }, [productData, isOpen]);


  const handleChange = (e) => {
    const { name, files } = e.target;
    // Arabic fields (nameAr, descriptionAr) only accept Arabic text
    const value = sanitizeArabicFieldEvent(e);

    if (name === "images") {
      if (!files || files.length === 0) return;
      if (formData.images.length + formData.tempImages.length + files.length > 5) {
        toast.error("You can only upload up to 5 images.", {
          position: "top-right",
          autoClose: 3000,
        });
        setErrors({ ...errors, images: "You can only upload up to 5 images." });
        return;
      }
      // Validate file size (3MB max per file)
      const validFiles = Array.from(files).filter((file) => {
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`File "${file.name}" exceeds 3MB limit.`, {
            position: "top-right",
            autoClose: 3000,
          });
          return false;
        }
        return true;
      });
      setFormData({ ...formData, tempImages: [...formData.tempImages, ...validFiles] });
      setErrors({ ...errors, images: "" });
    } else if (name.startsWith("colors")) {
      const [field, index, subField] = name.split(".");
      const updatedColors = [...formData.colors];
      if (subField === "stock" && value !== "" && (value < 0 || value > limits.colorStock)) return;
      updatedColors[index] = { ...updatedColors[index], [subField]: value };
      setFormData({ ...formData, colors: updatedColors });
      setErrors({ ...errors, colors: "" });
    } else if (name.startsWith("sizes")) {
      const [field, index, subField] = name.split(".");
      const updatedSizes = [...formData.sizes];
      if ((subField === "stock" || subField === "price") && value !== "" && (value < 0 || value > limits[subField === "stock" ? "sizeStock" : "sizePrice"])) return;
      updatedSizes[index] = { ...updatedSizes[index], [subField]: value };
      setFormData({ ...formData, sizes: updatedSizes });
      setErrors({ ...errors, sizes: "" });
    } else {
      if (name === "name" && value.length > limits.name) return;
      if (name === "nameAr" && value.length > limits.nameAr) return;
      if (name === "description" && value.length > limits.description) return;
      if (name === "descriptionAr" && value.length > limits.descriptionAr) return;
      if ((name === "price" || name === "stock" || name === "discount") && value !== "" && (value < 0 || value > limits[name])) return;
      let nextForm = { ...formData, [name]: value };
      // When discount type is fixed, discount cannot exceed (price - 1); e.g. price 1000 → max 999
      const fixedMax = formData.price !== "" && formData.price != null ? Math.max(0, parseFloat(formData.price) - 1) : null;
      if (name === "discount" && formData.discountType === "fixed" && fixedMax != null && value !== "" && parseFloat(value) > fixedMax) {
        nextForm[name] = String(fixedMax);
      }
      // When price is reduced and discount type is fixed, cap discount at (new price - 1)
      if (name === "price" && formData.discountType === "fixed" && formData.discount !== "" && formData.discount != null && value !== "" && parseFloat(formData.discount) > Math.max(0, parseFloat(value) - 1)) {
        nextForm.discount = String(Math.max(0, parseFloat(value) - 1));
      }
      setFormData(nextForm);
      setErrors({
        ...errors,
        [name]: "",
        ...(name === "discount" || name === "price" ? { discount: "" } : {}),
        ...(name === "stock" ? { colors: "", sizes: "" } : {}),
        ...(name === "discount" || name === "discountType" ? { sizes: "" } : {}),
      });
    }

  };

  const addColor = () => {
    setFormData({ ...formData, colors: [...formData.colors, { color: "#000000", stock: "" }] });
  };

  const removeColor = (index) => {
    setFormData({
      ...formData,
      colors: formData.colors.filter((_, i) => i !== index),
    });
  };

  const addSize = () => {
    setFormData({
      ...formData,
      sizes: [...formData.sizes, { size: "", stock: "", price: "" }],
    });
  };

  const removeSize = (index) => {
    setFormData({
      ...formData,
      sizes: formData.sizes.filter((_, i) => i !== index),
    });
  };

  const removeImage = async (index) => {
    const fileNameToRemove = formData.images[index];
    try {
      await removeFile(fileNameToRemove);
      setFormData({
        ...formData,
        images: formData.images.filter((_, i) => i !== index),
      });
    } catch (error) {
      console.error("Error removing image:", error);
      toast.error("Failed to remove image.", {
        position: "top-right",
        autoClose: 3000,
      });
      setErrors({ ...errors, images: "Failed to remove image" });
    }
  };

  const removeTempImage = (index) => {
    setFormData({
      ...formData,
      tempImages: formData.tempImages.filter((_, i) => i !== index),
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Product Name is required";
    if (!formData.nameAr) newErrors.nameAr = "Arabic Product Name is required";
    if (!formData.description) newErrors.description = "Description is required";
    if (!formData.descriptionAr) newErrors.descriptionAr = "Arabic Description is required";
    if (!formData.price) newErrors.price = "Price is required";
    if (!formData.stock) newErrors.stock = "Stock is required";
    if (formData.images.length === 0 && formData.tempImages.length === 0)
      newErrors.images = "At least one product image is required";
    if (formData.colors.length > 0 && formData.colors.some((c) => !c.color || !c.stock))
      newErrors.colors = "Each color must have a selected color and stock quantity";
    if (formData.sizes.length > 0 && formData.sizes.some((s) => !s.size || !s.stock || !s.price))
      newErrors.sizes = "Each size must have a name, stock quantity, and price";
    const mainStock = Number(formData.stock) || 0;
    if (formData.colors.length > 0) {
      const totalColorStock = formData.colors.reduce((sum, c) => sum + (Number(c.stock) || 0), 0);
      if (totalColorStock !== mainStock)
        newErrors.colors = newErrors.colors || "Total color stock must equal Stock quantity";
    }
    if (formData.sizes.length > 0) {
      const totalSizeStock = formData.sizes.reduce((sum, s) => sum + (Number(s.stock) || 0), 0);
      if (totalSizeStock !== mainStock)
        newErrors.sizes = newErrors.sizes || "Total size stock must equal Stock quantity";
    }
    if (formData.discountType === "fixed" && formData.discount !== "" && formData.discount != null && formData.sizes.length > 0) {
      const discountAmount = parseFloat(formData.discount) || 0;
      const hasSizePriceAtOrBelowDiscount = formData.sizes.some(
        (s) => s.price !== "" && s.price != null && (parseFloat(s.price) || 0) <= discountAmount
      );
      if (hasSizePriceAtOrBelowDiscount)
        newErrors.sizes = newErrors.sizes || "Size price should not be less than or equal to discount amount";
    }
    if (formData.discount && !formData.discountType) newErrors.discountType = "Discount Type is required when discount is provided";
    if (formData.discountType === "fixed" && formData.price !== "" && formData.discount !== "" && parseFloat(formData.discount) > Math.max(0, parseFloat(formData.price) - 1)) {
      newErrors.discount = "Discount cannot exceed (price - 1) when type is Fixed";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (validate()) {
      setLoading(true);
      try {
        const productDataToSend = {
          ...formData,
          merchant: merchantId,
          basePrice: formData.price,
          colors: formData.colors
            .filter((c) => c.color && c.stock)
            .map((c) => ({ color: c.color, stock: c.stock })),
          sizes: formData.sizes.filter((s) => s.size && s.stock && s.price),
        };

        if (formData.tempImages.length > 0) {
          const uploadedFileNames = await uploadMultipleFiles(formData.tempImages);
          productDataToSend.images = [...formData.images, ...uploadedFileNames];
        }
        delete productDataToSend.tempImages;
        delete productDataToSend.brand;

        if (productData) {
          await updateProduct(productData._id, productDataToSend);
        } else {
          await createProduct(productDataToSend);
        }
        toast.success("Product saved successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        onRefresh();
        toggle();
      } catch (error) {
        console.error("Error saving product:", error);
        toast.error(error.message || "Failed to save product", {
          position: "top-right",
          autoClose: 3000,
        });
        setErrors({ ...errors, general: error.message || "Failed to save product" });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="custom-modal" style={{ maxWidth: "70vw" }}>
      <ModalHeader toggle={toggle} className="text-white" style={{ background: "#33bdbf", color: "white" }}>
        {productData ? "Edit Product" : "Create Product"}
      </ModalHeader>
      <ModalBody className="p-4" style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <Form role="form" aria-label="Product form" className="custom-form">
          <Row className="form-sections">
            {/* Basic Information Section */}
            <Col md="12" className="form-section">
              <h6 className="section-header" role="heading" aria-level="3">
                Basic Information
              </h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="name" className="required-field">
                      Product Name (English)
                      <span className="sr-only">Required field</span>
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter Product Name"
                      maxLength={limits.name}
                      aria-describedby="name-help name-error"
                      aria-required="true"
                      aria-invalid={errors.name ? "true" : "false"}
                    />
                    <div className="character-count" id="name-help" aria-live="polite">
                      {formData?.name?.length}/{limits.name} characters
                    </div>
                    {errors.name && (
                      <div className="text-danger small mt-1" id="name-error" role="alert">
                        {errors.name}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="nameAr" className="required-field">
                      Product Name (Arabic)
                      <span className="sr-only">Required field</span>
                    </Label>
                    <Input
                      id="nameAr"
                      type="text"
                      name="nameAr"
                      value={formData.nameAr}
                      onChange={handleChange}
                      placeholder="أدخل اسم المنتج"
                      maxLength={limits.nameAr}
                      style={{ direction: "rtl" }}
                      aria-describedby="nameAr-help nameAr-error"
                      aria-required="true"
                      aria-invalid={errors.nameAr ? "true" : "false"}
                    />
                    <div className="character-count" id="nameAr-help" aria-live="polite">
                      {formData?.nameAr?.length}/{limits.nameAr} characters
                    </div>
                    {errors.nameAr && (
                      <div className="text-danger small mt-1" id="nameAr-error" role="alert">
                        {errors.nameAr}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="price" className="required-field">
                      Price (KWD)
                      <span className="sr-only">Required field</span>
                    </Label>
                    <Input
                      id="price"
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="Enter Price"
                      min={0}
                      max={limits.price}
                      step="0.01"
                      aria-describedby="price-help price-error"
                      aria-required="true"
                      aria-invalid={errors.price ? "true" : "false"}
                    />
                    <div className="input-help" id="price-help">
                      Max {limits.price} KWD
                    </div>
                    {errors.price && (
                      <div className="text-danger small mt-1" id="price-error" role="alert">
                        {errors.price}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="stock" className="required-field">
                      Stock Quantity
                      <span className="sr-only">Required field</span>
                    </Label>
                    <Input
                      id="stock"
                      type="number"
                      name="stock"
                      value={formData.stock}
                      onChange={handleChange}
                      placeholder="Enter Stock Quantity"
                      min={0}
                      max={limits.stock}
                      aria-describedby="stock-help stock-error"
                      aria-required="true"
                      aria-invalid={errors.stock ? "true" : "false"}
                    />
                    <div className="input-help" id="stock-help">
                      Max {limits.stock} units
                    </div>
                    {errors.stock && (
                      <div className="text-danger small mt-1" id="stock-error" role="alert">
                        {errors.stock}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="discount">
                      Discount
                      <span className="sr-only">Optional field</span>
                    </Label>
                    <Input
                      id="discount"
                      type="number"
                      name="discount"
                      value={formData.discount}
                      onChange={handleChange}
                      placeholder="Enter Discount"
                      min={0}
                      max={formData.discountType === "fixed" && formData.price ? Math.max(0, parseFloat(formData.price) - 1) : limits.discount}
                      step="0.01"
                      disabled={formData.price === "" || formData.price == null}
                      aria-describedby="discount-help discount-error"
                      aria-invalid={errors.discount ? "true" : "false"}
                    />
                    <div className="input-help" id="discount-help">
                      {formData.discountType === "fixed" && formData.price
                        ? `Max ${Math.max(0, parseFloat(formData.price) - 1)} KWD (price − 1)`
                        : `Max ${limits.discount} ${formData.discountType === "percentage" ? "%" : "KWD"}`}
                    </div>
                    {errors.discount && (
                      <div className="text-danger small mt-1" id="discount-error" role="alert">
                        {errors.discount}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="discountType">
                      Discount Type
                      <span className="sr-only">Optional field</span>
                    </Label>
                    <Input
                      id="discountType"
                      type="select"
                      name="discountType"
                      value={formData.discountType}
                      onChange={handleChange}
                      disabled={formData.price === "" || formData.price == null}
                      aria-describedby="discountType-error"
                      aria-invalid={errors.discountType ? "true" : "false"}
                    >
                      <option value="fixed">Fixed</option>
                      <option value="percentage">Percentage</option>
                    </Input>
                    {errors.discountType && (
                      <div className="text-danger small mt-1" id="discountType-error" role="alert">
                        {errors.discountType}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            </Col>

            {/* Descriptions Section */}
            <Col md="12" className="form-section description-section">
              <h6 className="section-header" role="heading" aria-level="3">
                Descriptions
              </h6>
              <FormGroup>
                <Label for="description" className="required-field">
                  Product Description (English)
                  <span className="sr-only">Required field</span>
                </Label>
                <Input
                  id="description"
                  type="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter Product Description"
                  maxLength={limits.description}
                  rows="4"
                  className="scrollable-textarea"
                  aria-describedby="description-help description-error"
                  aria-required="true"
                  aria-invalid={errors.description ? "true" : "false"}
                />
                <div className="character-count" id="description-help" aria-live="polite">
                  {formData?.description?.length}/{limits.description} characters
                </div>
                {errors.description && (
                  <div className="text-danger small mt-1" id="description-error" role="alert">
                    {errors.description}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="descriptionAr" className="required-field">
                  Product Description (Arabic)
                  <span className="sr-only">Required field</span>
                </Label>
                <Input
                  id="descriptionAr"
                  type="textarea"
                  name="descriptionAr"
                  value={formData.descriptionAr}
                  onChange={handleChange}
                  placeholder="أدخل وصف المنتج"
                  maxLength={limits.descriptionAr}
                  rows="4"
                  className="scrollable-textarea"
                  style={{ direction: "rtl" }}
                  aria-describedby="descriptionAr-help descriptionAr-error"
                  aria-required="true"
                  aria-invalid={errors.descriptionAr ? "true" : "false"}
                />
                <div className="character-count" id="descriptionAr-help" aria-live="polite">
                  {formData.descriptionAr?.length}/{limits.descriptionAr} characters
                </div>
                {errors.descriptionAr && (
                  <div className="text-danger small mt-1" id="descriptionAr-error" role="alert">
                    {errors.descriptionAr}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          {/* Category & Sub Category are inherited from merchant account settings */}

          {/* Colors Section */}
          <Row className="form-sections mt-4">
            <Col md="12" className="form-section">
              <h6 className="section-header" role="heading" aria-level="3">
                Colors (Optional)
              </h6>
              <Card className="border">
                <CardBody>
                  {formData.colors?.length === 0 && (
                    <p className="text-muted small mb-2">No colors added yet.</p>
                  )}
                  {formData.colors.map((color, index) => (
                    <div key={index} className="d-flex align-items-center mb-2">
                      <FormGroup className="me-2" style={{ width: "100px" }}>
                        <Input
                          type="color"
                          name={`colors.${index}.color`}
                          value={color.color}
                          onChange={handleChange}
                          className="form-control p-0"
                          style={{ height: "38px", width: "100%" }}
                          aria-label={`Color picker for color ${index + 1}`}
                        />
                      </FormGroup>
                      <FormGroup className="flex-fill me-2">
                        <Input
                          type="number"
                          name={`colors.${index}.stock`}
                          value={color.stock}
                          onChange={handleChange}
                          placeholder="Stock"
                          min={0}
                          max={limits.colorStock}
                          className="form-control"
                          aria-describedby={`color-stock-help-${index}`}
                        />
                        <div className="input-help" id={`color-stock-help-${index}`}>
                          Max {limits.colorStock} units
                        </div>
                      </FormGroup>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => removeColor(index)}
                        style={{
                          width: "30px",
                          height: "30px",
                          fontSize: "14px",
                          background: "#33bdbf",
                          borderRadius: "20px",
                          color: "white",
                          border: "none",
                          marginBottom: "30px",
                          padding: 0,
                        }}
                        aria-label={`Remove color ${index + 1}`}
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                  {errors.colors && (
                    <div className="text-danger small mt-1" role="alert">
                      {errors.colors}
                    </div>
                  )}
                  <Button color="primary" size="sm" onClick={addColor} className="mt-2">
                    Add Color
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Sizes Section */}
          <Row className="form-sections mt-4">
            <Col md="12" className="form-section">
              <h6 className="section-header" role="heading" aria-level="3">
                Sizes (Optional)
              </h6>
              <Card className="border">
                <CardBody>
                  {formData.sizes?.length === 0 && (
                    <p className="text-muted small mb-2">No sizes added yet.</p>
                  )}
                  {formData.sizes.map((size, index) => (
                    <div key={index} className="d-flex align-items-center mb-2">
                      <FormGroup className="flex-fill me-2">
                        <Input
                          type="text"
                          name={`sizes.${index}.size`}
                          value={size.size}
                          onChange={handleChange}
                          placeholder="Size (e.g., M)"
                          className="form-control"
                          aria-label={`Size name for size ${index + 1}`}
                        />
                        <div className="input-help">
                          Size Name
                        </div>
                      </FormGroup>

                      <FormGroup className="flex-fill me-2">
                        <Input
                          type="number"
                          name={`sizes.${index}.stock`}
                          value={size.stock}
                          onChange={handleChange}
                          placeholder="Stock"
                          min={0}
                          max={limits.sizeStock}
                          className="form-control"
                          aria-describedby={`size-stock-help-${index}`}
                        />
                        <div className="input-help" id={`size-stock-help-${index}`}>
                          Max {limits.sizeStock} units
                        </div>
                      </FormGroup>
                      <FormGroup className="flex-fill me-2">
                        <Input
                          type="number"
                          name={`sizes.${index}.price`}
                          value={size.price}
                          onChange={handleChange}
                          placeholder="Price (KWD)"
                          min={0}
                          max={limits.sizePrice}
                          step="0.01"
                          className="form-control"
                          aria-describedby={`size-price-help-${index}`}
                        />
                        <div className="input-help" id={`size-price-help-${index}`}>
                          Max {limits.sizePrice} KWD
                        </div>
                      </FormGroup>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => removeSize(index)}
                        style={{
                          width: "30px",
                          height: "30px",
                          fontSize: "14px",
                          background: "#33bdbf",
                          borderRadius: "20px",
                          color: "white",
                          border: "none",
                          marginBottom: "30px",
                          padding: 0,
                        }}
                        aria-label={`Remove size ${index + 1}`}
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                  {errors.sizes && (
                    <div className="text-danger small mt-1" role="alert">
                      {errors.sizes}
                    </div>
                  )}
                  <Button color="primary" size="sm" onClick={addSize} className="mt-2">
                    Add Size
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Media Upload Section */}
          <Row className="form-sections mt-4">
            <Col md="12" className="form-section">
              <h6 className="section-header" role="heading" aria-level="3">
                Media Upload
              </h6>
              <FormGroup>
                <Label for="images" className="required-field">
                  Upload Product Images
                  <span className="sr-only">Required field</span>
                </Label>
                <label
                  className="d-flex flex-column align-items-center justify-content-center border rounded p-2 bg-light image-upload-label"
                  style={{ height: "80px", width: "80px", cursor: "pointer" }}
                >
                  <UploadCloud size={20} color="#33bdbf" />
                  <small style={{ color: "#33bdbf" }}>Upload</small>
                  <Input
                    id="images"
                    type="file"
                    name="images"
                    multiple
                    onChange={handleChange}
                    accept="image/*"
                    hidden
                    aria-describedby="images-help images-error"
                    aria-required="true"
                    aria-invalid={errors.images ? "true" : "false"}
                  />
                </label>
                <div className="input-help" id="images-help">
                  Select multiple images (Max 3MB each, up to 5 images)
                </div>
                {errors.images && (
                  <div className="text-danger small mt-1" id="images-error" role="alert">
                    {errors.images}
                  </div>
                )}
              </FormGroup>
              {(formData.images?.length > 0 || formData.tempImages?.length > 0) && (
                <div className="mt-3">
                  <Label>Uploaded Images</Label>
                  <div className="image-gallery" role="group" aria-label="Uploaded product images">
                    {formData.images.map((fileName, index) => (
                      <div key={index} className="image-item">
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => removeImage(index)}
                          className="remove-btn"
                          aria-label={`Remove image ${index + 1}`}
                        >
                          ✕
                        </Button>
                        <img
                          src={`${FILE_BASE_URL}/${fileName}`}
                          alt={`Product image ${index + 1}`}
                          className="img-thumbnail"
                        />
                      </div>
                    ))}
                    {formData.tempImages.map((file, index) => (
                      <div key={`temp-${index}`} className="image-item">
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => removeTempImage(index)}
                          className="remove-btn"
                          aria-label={`Remove preview image ${index + 1}`}
                        >
                          ✕
                        </Button>
                        <img
                          src={URL.createObjectURL(file) || "/placeholder.svg"}
                          alt={`Product preview image ${index + 1}`}
                          className="img-thumbnail"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Col>
          </Row>

          {errors.general && (
            <Row className="mt-3">
              <Col md="12">
                <div className="alert alert-danger" role="alert" aria-live="assertive">
                  <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
                  {errors.general}
                </div>
              </Col>
            </Row>
          )}
        </Form>
      </ModalBody>
      <ModalFooter className="border-top">
        <Button
          color="secondary"
          onClick={toggle}
          aria-label="Cancel and close"
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSave}
          disabled={loading}
          aria-label={loading ? "Saving product data" : "Save product data"}
          className="save-btn"
        >
          {loading ? (
            <>
              Saving...
            </>
          ) : (
            "Save Product"
          )}
        </Button>
      </ModalFooter>
      <ToastContainer />
    </Modal>
  );
};

export default ProductFormModal;